import { useEffect, useState, useCallback } from "react"
import { getPossibleInsourceExecutors, SimpleExecutorRecord } from "src/services/api/document"
import { Community } from "@models/user"

interface UseExecutorSelectionProps {
  communities: Community[]
  enhancer?: SimpleExecutorRecord
  trustminer?: SimpleExecutorRecord
  onEnhancerChange: (enhancer: SimpleExecutorRecord) => void
  onTrustminerChange: (trustminer: SimpleExecutorRecord) => void
  withTrusminer?: boolean
}

interface UseExecutorSelectionReturn {
  executorList: SimpleExecutorRecord[]
  handleEnhancerChange: (executor: SimpleExecutorRecord) => void
  handleTrustminerChange: (executor: SimpleExecutorRecord) => void
}

/**
 * Custom hook for managing executor selection logic
 * Handles fetching executors, sorting them, and managing selection conflicts
 */
export const useExecutorSelection = ({
  communities,
  enhancer,
  trustminer,
  onEnhancerChange,
  onTrustminerChange,
  withTrusminer
}: UseExecutorSelectionProps): UseExecutorSelectionReturn => {
  const [executorList, setExecutorList] = useState<SimpleExecutorRecord[]>([])

  const sortByFirstName = useCallback((a: SimpleExecutorRecord, b: SimpleExecutorRecord) => {
    return a.firstName.localeCompare(b.firstName)
  }, [])

  const firstNotSelectedExecutor = useCallback((
    executorList: SimpleExecutorRecord[],
    excluded: SimpleExecutorRecord | undefined
  ) => {
    return executorList.filter((e) => e.id !== excluded?.id)[0]
  }, [])

  // Fetch and sort executors based on communities
  useEffect(() => {
    if (communities.length > 0) {
      getPossibleInsourceExecutors(communities.map((c) => c.gatedCommunityId))
        .then((possibleExecutors) => possibleExecutors.sort(sortByFirstName))
        .then((sortedExecutors) => {
          setExecutorList(sortedExecutors)
          
          // Auto-select enhancer if current one is not in the list
          if (enhancer && sortedExecutors.filter((e) => e.id === enhancer.id).length === 0) {
            onEnhancerChange(firstNotSelectedExecutor(sortedExecutors, trustminer))
          }
          
          // Auto-select trustminer if current one is not in the list
          if (trustminer && sortedExecutors.filter((e) => e.id === trustminer.id).length === 0) {
            onTrustminerChange(firstNotSelectedExecutor(sortedExecutors, enhancer))
          }
          // Sync default trustminer to state when user never changed the dropdown
          if (withTrusminer && !trustminer && sortedExecutors.length > 0) {
            onTrustminerChange(firstNotSelectedExecutor(sortedExecutors, enhancer))
          }
        })
    }
  }, [communities, enhancer, trustminer, withTrusminer, onEnhancerChange, onTrustminerChange, sortByFirstName, firstNotSelectedExecutor])

  const handleEnhancerChange = useCallback((executor: SimpleExecutorRecord) => {
    // If trustminer is the same as new enhancer, change trustminer to avoid conflict
    if (trustminer?.id === executor.id) {
      onTrustminerChange(firstNotSelectedExecutor(executorList, executor))
    }
    onEnhancerChange(executor)
  }, [executorList, trustminer, onEnhancerChange, onTrustminerChange, firstNotSelectedExecutor])

  const handleTrustminerChange = useCallback((executor: SimpleExecutorRecord) => {
    // If enhancer is the same as new trustminer, change enhancer to avoid conflict
    if (enhancer?.id === executor.id) {
      onEnhancerChange(firstNotSelectedExecutor(executorList, executor))
    }
    onTrustminerChange(executor)
  }, [executorList, enhancer, onEnhancerChange, onTrustminerChange, firstNotSelectedExecutor])

  return {
    executorList,
    handleEnhancerChange,
    handleTrustminerChange
  }
}
